
import React, { useEffect, useState } from 'react';
import { PermissionNotificationService } from '@/services/permissions/notifications/permissionNotificationService';
import { PermissionNotification } from '@/types/permissionErrors';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { X, AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react';

export const PermissionNotificationDisplay: React.FC = () => {
  const [notifications, setNotifications] = useState<PermissionNotification[]>([]);

  useEffect(() => {
    const unsubscribe = PermissionNotificationService.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const getIcon = (type: PermissionNotification['type']) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="h-4 w-4" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />;
      case 'info':
        return <Info className="h-4 w-4" />;
      case 'success':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getVariant = (type: PermissionNotification['type']) => {
    switch (type) {
      case 'error':
        return 'destructive';
      case 'warning':
        return 'default';
      case 'info':
        return 'default';
      case 'success':
        return 'default';
      default:
        return 'default';
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 w-96 max-w-[calc(100vw-2rem)]">
      {notifications.map((notification: PermissionNotification) => (
        <Alert 
          key={notification.id} 
          variant={getVariant(notification.type)}
          className="shadow-lg border-l-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-2 flex-1">
              {getIcon(notification.type)}
              <div className="flex-1">
                <AlertTitle className="text-sm font-medium">
                  {notification.title}
                </AlertTitle>
                <AlertDescription className="text-sm mt-1">
                  {notification.message}
                </AlertDescription>
                
                {notification.actions && notification.actions.length > 0 && (
                  <div className="flex gap-2 mt-3">
                    {notification.actions.map((action: { label: string; action: () => void; variant?: 'default' | 'destructive' }, index: number) => (
                      <Button
                        key={index}
                        size="sm"
                        variant={action.variant || 'default'}
                        onClick={action.action}
                        className="text-xs"
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {!notification.persistent && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-70 hover:opacity-100"
                onClick={() => PermissionNotificationService.removeNotification(notification.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </Alert>
      ))}
    </div>
  );
};
